import { useState, useEffect, type ChangeEvent, type MouseEvent } from "react";
import { TreeView } from "@mui/x-tree-view/TreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getOrganizations } from "../../../../api/organizations";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import ArrowCircleLeftOutlinedIcon from "@mui/icons-material/ArrowCircleLeftOutlined";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import SnackbarCustom, {
  type SnackbarData,
} from "../../../../components/SnackBarCustom";
import {
  checkRecursiveParents,
  flattenOrganizationIds,
  getOrganizationLabel,
  getParentOrganizationId,
  hasLevelOne,
  type OrganizationIdsState,
  type OrganizationTreeItem,
  type SetOrganizationIds,
} from "./organizationTableTypes";

type DisconnectedTableColumn = {
  id: keyof OrganizationTreeItem;
  label: string;
  minWidth: string;
  align: "left";
  isSmallScreen: boolean;
};

const columns: DisconnectedTableColumn[] = [
  {
    id: "department_name",
    label: "部名",
    minWidth: "fit-content",
    align: "left",
    isSmallScreen: false,
  },
];

type DisconnectedTableProps = {
  CheckedItems: SetOrganizationIds;
  disconnectedOrganizations: OrganizationIdsState;
  update?: boolean | string;
};

type CheckboxValidateProps = {
  organization: OrganizationTreeItem;
  organizationIds: number[];
  organizations: OrganizationTreeItem[];
  disconnectedOrganizations: OrganizationIdsState;
  CheckedItems: SetOrganizationIds;
};

const CheckboxValidate = ({
  organization,
  organizationIds,
  organizations,
  disconnectedOrganizations,
  CheckedItems,
}: CheckboxValidateProps) => {
  const orgId = organization.id;
  const parentId = getParentOrganizationId(organization);

  const isChecked =
    disconnectedOrganizations.m_organization_ids?.includes(orgId) ||
    organizationIds.some((id) =>
      disconnectedOrganizations.m_organization_ids?.includes(id)
    ) ||
    false;

  const handleParentChildCheck = (
    checked: boolean,
    ids: number[],
    parentOrgId: number | null
  ) => {
    CheckedItems((prevState) => {
      const currentIds = prevState.m_organization_ids || [];
      let updatedIds = [...currentIds];

      if (checked) {
        updatedIds = Array.from(new Set([...updatedIds, ...ids]));
      } else {
        updatedIds = updatedIds.filter((id) => !ids.includes(id));
      }

      if (parentOrgId) {
        updatedIds = checkRecursiveParents(
          parentOrgId,
          organizations,
          updatedIds
        );
      }

      return {
        ...prevState,
        m_organization_ids: updatedIds,
      };
    });
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleParentChildCheck(e.target.checked, organizationIds, parentId);
  };

  return (
    <Checkbox
      name="checkButtonConnect"
      checked={isChecked}
      icon={<ArrowCircleLeftOutlinedIcon />}
      checkedIcon={<ArrowCircleLeftIcon />}
      sx={{ pointerEvents: "auto" }}
      onClick={(e: MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
      onChange={handleChange}
    />
  );
};

const DisconnectedTable = ({
  CheckedItems,
  disconnectedOrganizations,
  update,
}: DisconnectedTableProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [organizations, setOrganizations] = useState<OrganizationTreeItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const fetchOrganizations = async () => {
    try {
      const response = await getOrganizations({
        parent_m_organization_id: 0,
        m_branch_id: null,
        m_role_id: 0,
        order: null,
        sort: null,
        page: page + 1,
        limit: rowsPerPage,
      });
      if (response.status === "ok") {
        const data = response.m_organizations as OrganizationTreeItem[];
        const branchName = data.map((dataSingle) => dataSingle.m_branch.name);
        const dataCombined = data.map((item, index) => ({
          ...item,
          m_branch_name: branchName[index],
        }));
        console.log("DisconnectedTable data:", dataCombined);
        setOrganizations(dataCombined);
        setTotalCount(response.total_count);
        setSnackBarstatus(response);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  useEffect(() => {
    void fetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update, rowsPerPage, page]);

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const renderTree = (organization: OrganizationTreeItem, level = 0) => {
    const organizationIds = flattenOrganizationIds(organization);
    if (!organization) {
      return null;
    }

    const filteredChildren = Array.isArray(organization.child_m_organizations)
      ? (organization.child_m_organizations as OrganizationTreeItem[]).filter(
          (childOrg) => hasLevelOne(childOrg)
        )
      : [];

    return (
      <TreeItem
        key={organization.id}
        nodeId={String(organization.id)}
        data-level-connect={level}
        label={
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CheckboxValidate
              organization={organization}
              organizationIds={organizationIds}
              organizations={organizations}
              disconnectedOrganizations={disconnectedOrganizations}
              CheckedItems={CheckedItems}
            />
            {getOrganizationLabel(organization)}
          </Box>
        }
        sx={{
          display: "flex",
          flexDirection: "column",
          ".MuiTreeItem-iconContainer": {
            display: "none",
          },
          ".css-1vbkhv8-MuiTreeItem-content": {
            padding: "0",
          },
          ".css-1vbkhv8-MuiTreeItem-content .MuiTreeItem-label": {
            padding: "0",
          },
        }}
      >
        {filteredChildren.length > 0
          ? filteredChildren.map((childOrganization) =>
              renderTree(childOrganization, level + 1)
            )
          : null}
      </TreeItem>
    );
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
      <TreeView
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpandIcon={<ChevronRightIcon />}
        multiSelect
      >
        {organizations
          .filter(
            (organization) => organization.parent_m_organization_id === null
          )
          .filter((organization) => hasLevelOne(organization))
          .map((organization) => renderTree(organization))}
      </TreeView>
      <Dialog open={isPopoverOpen} onClose={handlePopoverClose}>
        <Box
          sx={{
            display: "flex",
            height: "56px",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px",
            borderBottom: "2px solid #CCC",
          }}
        >
          <Box
            sx={{
              paddingLeft: "8px",
              fontWeight: "bold",
              fontSize: "20px",
            }}
          >
            未関連組織詳細
          </Box>
          <IconButton
            aria-label="close"
            onClick={handlePopoverClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
          <TableBody>
            {columns.map((column) => (
              <TableRow role="checkbox" tabIndex={-1} key={column.id}>
                <TableCell
                  align={column.align}
                  sx={{
                    color: "outline.dark",
                    fontWeight: "bold",
                    boxSizing: "border-box",
                    width: "calc((100vw - 64px) / 2)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      WebkitWritingMode: "horizontal-tb",
                      height: "100%",
                      padding: "8px",
                    }}
                  >
                    {column.label}
                  </Box>
                </TableCell>
                <TableCell
                  align={column.align}
                  sx={{
                    writingMode: "horizontal-tb",
                    boxSizing: "border-box",
                    width: "calc((100vw - 64px) / 2)",
                    overflowWrap: "break-word",
                  }}
                />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Dialog>
      <TablePagination
        rowsPerPageOptions={[10, 30, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          ".MuiToolbar-root,.MuiInputBase-root,.MuiTablePagination-actions": {
            margin: "0 !important",
            padding: "0 !important",
          },
        }}
        labelRowsPerPage="表示件数:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} / ${count !== -1 ? count : `more than ${to}`}`
        }
      />
      <SnackbarCustom
        data={snackBarstatus}
        openStatus={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
      />
    </Paper>
  );
};

export default DisconnectedTable;
